import {
    CheckCircleOutlined,
    EditOutlined,
    PoweroffOutlined
} from '@ant-design/icons';
import {
    DatePicker,
    Dropdown,
    message,
    Modal,
    Table,
    Tooltip
} from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ClientBulkEditPanel from '../components/client-bulk-edit-panel';
import ClientCreatePanel from '../components/client-create-panel';
import ClientDetailDrawer from '../components/client-detail-drawer/client-detail-drawer';
import { apiService } from '../services/api';
import '../styles/sticky-columns.css';
import '../styles/clients.css';
import { normalizeClientCabinetEmailDisplay } from '../utils/normalizeClientCabinetEmailDisplay';
import { openAppPathInNewTab } from '../utils/openAppPathInNewTab';
import { isPrimarySpaNavigation } from '../utils/spaLinkUtils';

const buildClientProxiesPath = (client) => {
  if (!client?.login) {
    return null;
  }
  const params = new URLSearchParams();
  params.set('client_login', client.login);
  params.set('page', '1');
  return `/proxies?${params.toString()}`;
};

/**
 * Строка таблицы «Клиенты» может быть старее, чем снимок из GET /api/clients/:id в drawer.
 * Не даём явным null/пустым полям профиля перезатирать уже загруженные ненепустые значения.
 */
const mergeDetailClientWithListRow = (prev, fresh) => {
  if (!prev || !fresh) {
    return prev;
  }
  const next = { ...prev, ...fresh };
  const keys = ['last_name', 'first_name', 'middle_name', 'phone', 'telegram_username'];
  for (const k of keys) {
    const f = fresh[k];
    const p = prev[k];
    const fEmpty = f == null || (typeof f === 'string' && f.trim() === '');
    const pNonEmpty = p != null && !(typeof p === 'string' && p.trim() === '');
    if (fEmpty && pNonEmpty) {
      next[k] = p;
    }
  }
  return next;
};

// Flaticon SVG icons
const DeleteIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.5 2.25H8.625V1.875C8.625 1.37772 8.42746 0.900806 8.07582 0.549175C7.72419 0.197544 7.24728 0 6.75 0H5.25C4.75272 0 4.27581 0.197544 3.92418 0.549175C3.57254 0.900806 3.375 1.37772 3.375 1.875V2.25H1.5C1.40054 2.25 1.30516 2.28951 1.23484 2.35984C1.16451 2.43016 1.125 2.52554 1.125 2.625V3C1.125 3.09946 1.16451 3.19484 1.23484 3.26517C1.30516 3.33549 1.40054 3.375 1.5 3.375H1.875V10.125C1.875 10.4234 1.99353 10.7095 2.2045 10.9205C2.41548 11.1315 2.70163 11.25 3 11.25H9C9.29837 11.25 9.58452 11.1315 9.7955 10.9205C10.0065 10.7095 10.125 10.4234 10.125 10.125V3.375H10.5C10.5995 3.375 10.6948 3.33549 10.7652 3.26517C10.8355 3.19484 10.875 3.09946 10.875 3V2.625C10.875 2.52554 10.8355 2.43016 10.7652 2.35984C10.6948 2.28951 10.5995 2.25 10.5 2.25ZM4.5 1.875C4.5 1.67609 4.57902 1.48532 4.71967 1.34467C4.86032 1.20402 5.05109 1.125 5.25 1.125H6.75C6.94891 1.125 7.13968 1.20402 7.28033 1.34467C7.42098 1.48532 7.5 1.67609 7.5 1.875V2.25H4.5V1.875ZM9 10.125H3V3.375H9V10.125Z" fill="currentColor"/>
  </svg>
);

const ReloadIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_2040_24875)">
      <path d="M6.0001 0.999894C6.66336 1.00207 7.31959 1.13576 7.93084 1.39324C8.54208 1.65071 9.09619 2.02685 9.5611 2.49989H8.0001C7.86749 2.49989 7.74032 2.55257 7.64655 2.64634C7.55278 2.74011 7.5001 2.86729 7.5001 2.99989C7.5001 3.1325 7.55278 3.25968 7.64655 3.35345C7.74032 3.44722 7.86749 3.49989 8.0001 3.49989H10.0716C10.3178 3.49976 10.5539 3.4019 10.728 3.2278C10.9021 3.0537 11 2.81761 11.0001 2.57139V0.499894C11.0001 0.367286 10.9474 0.240109 10.8537 0.14634C10.7599 0.0525723 10.6327 -0.000106158 10.5001 -0.000106158C10.3675 -0.000106158 10.2403 0.0525723 10.1465 0.14634C10.0528 0.240109 10.0001 0.367286 10.0001 0.499894V1.53889C9.17369 0.79462 8.15639 0.295099 7.06215 0.0962684C5.96791 -0.102562 4.83991 0.00714982 3.80449 0.413118C2.76907 0.819085 1.86711 1.50528 1.19961 2.39486C0.532105 3.28443 0.125422 4.34227 0.0251013 5.44989C0.0186429 5.51952 0.0267591 5.58973 0.0489327 5.65605C0.0711063 5.72237 0.106851 5.78334 0.153889 5.83509C0.200928 5.88683 0.258228 5.92821 0.32214 5.95658C0.386052 5.98496 0.455174 5.99971 0.525101 5.99989C0.647395 6.00145 0.765868 5.95731 0.857331 5.87612C0.948793 5.79492 1.00666 5.68251 1.0196 5.56089C1.13091 4.31627 1.70359 3.15825 2.62514 2.31431C3.54669 1.47038 4.75051 1.00154 6.0001 0.999894Z" fill="currentColor"/>
      <path d="M11.4756 6.00004C11.3533 5.99848 11.2349 6.04262 11.1434 6.12382C11.0519 6.20501 10.9941 6.31742 10.9811 6.43904C10.8985 7.39066 10.5446 8.2986 9.96145 9.05515C9.37831 9.8117 8.5904 10.3851 7.6912 10.7073C6.79199 11.0296 5.81922 11.0871 4.8883 10.8731C3.95738 10.6591 3.10737 10.1826 2.43912 9.50005H4.00012C4.13273 9.50005 4.25991 9.44737 4.35368 9.3536C4.44744 9.25983 4.50012 9.13266 4.50012 9.00005C4.50012 8.86744 4.44744 8.74026 4.35368 8.64649C4.25991 8.55272 4.13273 8.50005 4.00012 8.50005H1.92862C1.80667 8.49998 1.6859 8.52395 1.57322 8.57059C1.46054 8.61723 1.35816 8.68562 1.27193 8.77185C1.18569 8.85808 1.1173 8.96047 1.07067 9.07315C1.02403 9.18583 1.00006 9.3066 1.00012 9.42855V11.5001C1.00012 11.6327 1.0528 11.7598 1.14657 11.8536C1.24034 11.9474 1.36751 12.0001 1.50012 12.0001C1.63273 12.0001 1.75991 11.9474 1.85368 11.8536C1.94744 11.7598 2.00012 11.6327 2.00012 11.5001V10.4611C2.82654 11.2053 3.84383 11.7048 4.93807 11.9037C6.03232 12.1025 7.16031 11.9928 8.19573 11.5868C9.23115 11.1809 10.1331 10.4947 10.8006 9.60509C11.4681 8.71551 11.8748 7.65767 11.9751 6.55004C11.9816 6.48041 11.9735 6.4102 11.9513 6.34388C11.9291 6.27756 11.8934 6.21659 11.8463 6.16485C11.7993 6.11311 11.742 6.07173 11.6781 6.04335C11.6142 6.01498 11.545 6.00023 11.4751 6.00004H11.4756Z" fill="currentColor"/>
    </g>
    <defs>
      <clipPath id="clip0_2040_24875">
        <rect width="12" height="12" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const SyncIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.1784 1.46003L4.4704 2.61C4.3975 2.7151 4.2871 2.7883 4.1618 2.8145C4.0366 2.8406 3.9061 2.8178 3.7972 2.7506C3.6883 2.6835 3.6093 2.5772 3.5764 2.4536C3.5435 2.3299 3.5593 2.1984 3.6204 2.086L4.3284 0.93603C4.506 0.65254 4.7526 0.41881 5.0453 0.25679C5.3379 0.09477 5.6669 0.00977 6.0014 0.00977C6.3359 0.00977 6.6649 0.09477 6.9576 0.25679C7.2502 0.41881 7.4969 0.65254 7.6744 0.93603L9.7744 4.353L9.9949 3.1705C10.0192 3.0401 10.0943 2.9247 10.2037 2.8496C10.313 2.7746 10.4478 2.746 10.5782 2.7703C10.7086 2.7945 10.824 2.8696 10.8991 2.979C10.9742 3.0884 11.0027 3.2231 10.9784 3.3535L10.7284 4.6885C10.6645 5.0322 10.4825 5.3427 10.2139 5.5665C9.9453 5.7903 9.607 5.9132 9.2574 5.914C9.1599 5.914 7.6489 5.626 7.6489 5.626C7.521 5.5982 7.4091 5.5214 7.3372 5.412C7.2654 5.3026 7.2393 5.1694 7.2645 5.0409C7.2898 4.9125 7.3644 4.7991 7.4724 4.7251C7.5803 4.651 7.713 4.6223 7.8419 4.645L8.9084 4.8555L6.8214 1.46003C6.7347 1.3203 6.6137 1.205 6.4699 1.12506C6.3262 1.04511 6.1644 1.00316 5.9999 1.00316C5.8354 1.00316 5.6737 1.04511 5.5299 1.12506C5.3862 1.205 5.2652 1.3203 5.1784 1.46003ZM11.7044 7.4895L11.2204 6.7035C11.188 6.6439 11.1437 6.5914 11.0903 6.5494C11.0368 6.5075 10.9754 6.4768 10.9098 6.4593C10.8441 6.4419 10.7756 6.438 10.7084 6.4479C10.6412 6.4578 10.5767 6.4812 10.5189 6.5169C10.461 6.5525 10.4111 6.5996 10.372 6.6552C10.333 6.7108 10.3057 6.7738 10.2918 6.8403C10.2779 6.9068 10.2777 6.9754 10.2912 7.042C10.3048 7.1086 10.3317 7.1717 10.3704 7.2275L10.8544 8.0135C10.9468 8.1614 10.9974 8.3314 11.001 8.5057C11.0046 8.68 10.9611 8.852 10.8749 9.0035C10.7921 9.1547 10.6699 9.2806 10.5213 9.3679C10.3727 9.4552 10.2033 9.5007 10.0309 9.4995H6.3379L6.9734 8.865C7.0646 8.7709 7.1152 8.6446 7.1143 8.5135C7.1133 8.3824 7.0609 8.2569 6.9683 8.1641C6.8758 8.0713 6.7504 8.0185 6.6193 8.0172C6.4882 8.0159 6.3619 8.0661 6.2674 8.157L5.4214 8.9995C5.1574 9.2621 5.0062 9.6172 4.9999 9.9895C4.9999 9.9895 4.9999 9.9945 4.9999 9.9965C4.9999 9.9985 4.9999 10.003 4.9999 10.0065C4.9985 10.1989 5.0356 10.3897 5.109 10.5675C5.1824 10.7453 5.2907 10.9067 5.4274 11.042L6.2684 11.868C6.3148 11.9163 6.3703 11.9548 6.4318 11.9813C6.4933 12.0077 6.5595 12.0215 6.6264 12.0219C6.6934 12.0223 6.7597 12.0092 6.8215 11.9835C6.8833 11.9577 6.9393 11.9198 6.9862 11.8721C7.033 11.8243 7.0698 11.7676 7.0944 11.7053C7.1189 11.643 7.1307 11.5765 7.1291 11.5095C7.1274 11.4426 7.1124 11.3767 7.0848 11.3157C7.0572 11.2548 7.0176 11.1999 6.9684 11.1545L6.3044 10.4995H10.0309C10.3817 10.4983 10.7258 10.4037 11.0279 10.2253C11.3299 10.047 11.579 9.7913 11.7494 9.4848C11.9199 9.1782 12.0055 8.8317 11.9976 8.481C11.9897 8.1304 11.8885 7.7881 11.7044 7.4895ZM3.3899 3.606C3.2261 3.4944 3.0416 3.4166 2.8473 3.3771C2.653 3.3376 2.4529 3.3373 2.2584 3.376L0.94393 3.635C0.87844 3.6466 0.81592 3.6711 0.76002 3.7071C0.70412 3.7431 0.65596 3.7899 0.61836 3.8447C0.58076 3.8995 0.55447 3.9613 0.54104 4.0264C0.52761 4.0916 0.52731 4.1587 0.54014 4.224C0.55298 4.2892 0.5787 4.3512 0.61579 4.4064C0.65289 4.4616 0.70062 4.5088 0.7562 4.5453C0.81177 4.5818 0.87406 4.6069 0.93944 4.619C1.00481 4.6312 1.07195 4.6302 1.13693 4.616L2.1909 4.4085L0.29543 7.4895C0.11135 7.7881 0.01014 8.1303 0.00221 8.4809C-0.00572 8.8316 0.07992 9.178 0.25031 9.4845C0.4207 9.7911 0.6697 10.0467 0.97167 10.2251C1.27364 10.4035 1.6177 10.4982 1.96843 10.4995H3.5399C3.6725 10.4995 3.7997 10.4469 3.8935 10.3531C3.9872 10.2593 4.0399 10.1321 4.0399 9.9995C4.0399 9.8669 3.9872 9.7397 3.8935 9.646C3.7997 9.5522 3.6725 9.4995 3.5399 9.4995H1.96843C1.79642 9.5003 1.62738 9.4547 1.47918 9.3674C1.33097 9.2801 1.2091 9.1544 1.12643 9.0035C1.0403 8.852 0.99673 8.68 1.00034 8.5057C1.00395 8.3314 1.0546 8.1614 1.14693 8.0135L3.0659 4.8945L3.2889 6.0915C3.3133 6.222 3.3885 6.3373 3.498 6.4123C3.6075 6.4873 3.7423 6.5157 3.8727 6.4913C4.0031 6.4669 4.1185 6.3917 4.1934 6.2822C4.2684 6.1727 4.2968 6.038 4.2724 5.9075L4.0224 4.5725C3.9865 4.3775 3.9121 4.1916 3.8035 4.0256C3.6949 3.8597 3.5543 3.717 3.3899 3.606Z" fill="currentColor"/>
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.5 5.5H6.5V0.5C6.5 0.367 6.447 0.24 6.354 0.146C6.26 0.053 6.133 0 6 0C5.867 0 5.74 0.053 5.646 0.146C5.553 0.24 5.5 0.367 5.5 0.5V5.5H0.5C0.367 5.5 0.24 5.553 0.146 5.646C0.053 5.74 0 5.867 0 6C0 6.133 0.053 6.26 0.146 6.354C0.24 6.447 0.367 6.5 0.5 6.5H5.5V11.5C5.5 11.633 5.553 11.76 5.646 11.854C5.74 11.947 5.867 12 6 12C6.133 12 6.26 11.947 6.354 11.854C6.447 11.76 6.5 11.633 6.5 11.5V6.5H11.5C11.633 6.5 11.76 6.447 11.854 6.354C11.947 6.26 12 6.133 12 6C12 5.867 11.947 5.74 11.854 5.646C11.76 5.553 11.633 5.5 11.5 5.5Z" fill="currentColor"/>
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.33 0.4652L3.23 6.5612C3 6.7927 2.81 7.0682 2.69 7.3716C2.56 7.675 2.5 8.0003 2.5 8.3287V9.0002C2.5 9.1328 2.55 9.2599 2.65 9.3537C2.74 9.4475 2.87 9.5002 3 9.5002H3.67C4 9.5011 4.33 9.4369 4.63 9.3112C4.93 9.1856 5.21 9.001 5.44 8.7682L11.54 2.6722C11.83 2.3792 11.99 1.9824 11.99 1.5687C11.99 1.1549 11.83 0.7581 11.54 0.4652C11.24 0.1811 10.84 0.0226 10.43 0.0226C10.02 0.0226 9.63 0.1811 9.33 0.4652ZM10.83 1.9652L4.73 8.0612C4.45 8.3414 4.07 8.4991 3.67 8.5002H3.5V8.3287C3.5 7.9312 3.66 7.5501 3.94 7.2682L10.04 1.1722C10.14 1.0701 10.28 1.0132 10.43 1.0132C10.58 1.0132 10.72 1.0701 10.83 1.1722C10.93 1.2774 10.99 1.42 10.99 1.5687C10.99 1.7173 10.93 1.8599 10.83 1.9652Z" fill="currentColor"/>
    <path d="M11.5 4.4895C11.37 4.4895 11.24 4.5422 11.15 4.6359C11.05 4.7297 11 4.8569 11 4.9895V7.5H9C8.6 7.5 8.22 7.658 7.94 7.9393C7.66 8.2206 7.5 8.6022 7.5 9V11H2.5C2.1 11 1.72 10.842 1.44 10.5607C1.16 10.2794 1 9.8978 1 9.5V2.5C1 2.1022 1.16 1.7206 1.44 1.4393C1.72 1.158 2.1 1 2.5 1H7.02C7.15 1 7.28 0.9473 7.37 0.8536C7.47 0.7598 7.52 0.6326 7.52 0.5C7.52 0.3674 7.47 0.2402 7.37 0.1464C7.28 0.0527 7.15 0 7.02 0H2.5C1.84 0.0008 1.2 0.2644 0.73 0.7331C0.26 1.2018 0 1.8372 0 2.5V9.5C0 10.1628 0.26 10.7982 0.73 11.2669C1.2 11.7356 1.84 11.9992 2.5 12H8.17C8.5 12.0009 8.83 11.9367 9.13 11.8111C9.43 11.6854 9.71 11.5008 9.94 11.268L11.27 9.939C11.5 9.7074 11.69 9.432 11.81 9.1286C11.94 8.8252 12 8.4999 12 8.1715V4.9895C12 4.8569 11.95 4.7297 11.85 4.6359C11.76 4.5422 11.63 4.4895 11.5 4.4895ZM9.23 10.561C9.03 10.7615 8.78 10.9003 8.5 10.961V9C8.5 8.8674 8.55 8.7402 8.65 8.6464C8.74 8.5527 8.87 8.5 9 8.5H10.96C10.9 8.7768 10.76 9.0305 10.56 9.232L9.23 10.561Z" fill="currentColor"/>
  </svg>
);

const ClientsFilterOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 4H15.5L9 13V19L7 17V13L3 7.58V4Z" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17 8L22 13" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    <path d="M22 8L17 13" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
);

const FilterFunnelIcon = ({ filtered }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ color: filtered ? '#5283f4' : '#7a7a7a' }}
  >
    <path d="M9.00005 14C8.89187 14 8.7866 13.9649 8.70005 13.9L6.70005 12.4C6.63795 12.3534 6.58755 12.293 6.55284 12.2236C6.51812 12.1542 6.50005 12.0776 6.50005 12V9.19L2.99205 5.2435C2.74291 4.96244 2.58024 4.61541 2.52359 4.24412C2.46694 3.87283 2.51872 3.49308 2.67271 3.15051C2.8267 2.80794 3.07635 2.51713 3.39164 2.31303C3.70694 2.10894 4.07446 2.00024 4.45005 2H11.5501C11.9256 2.00044 12.293 2.10932 12.6082 2.31355C12.9234 2.51779 13.1729 2.80869 13.3267 3.15129C13.4805 3.4939 13.5321 3.87364 13.4753 4.24487C13.4185 4.61611 13.2558 4.96305 13.0066 5.244L9.50005 9.19V13.5C9.50005 13.6326 9.44737 13.7598 9.3536 13.8536C9.25984 13.9473 9.13266 14 9.00005 14ZM7.50005 11.75L8.50005 12.5V9C8.50015 8.87758 8.54516 8.75945 8.62655 8.668L12.2606 4.5795C12.3817 4.44254 12.4608 4.27352 12.4883 4.09273C12.5157 3.91193 12.4905 3.72705 12.4154 3.56028C12.3404 3.39351 12.2189 3.25193 12.0654 3.15254C11.9118 3.05316 11.7329 3.00019 11.5501 3H4.45005C4.26728 3.00028 4.08846 3.05327 3.93505 3.15263C3.78164 3.25199 3.66015 3.3935 3.58516 3.56018C3.51016 3.72685 3.48483 3.91163 3.51221 4.09234C3.5396 4.27305 3.61854 4.44203 3.73955 4.579L7.37405 8.668C7.45526 8.75952 7.50009 8.87764 7.50005 9V11.75Z" fill="currentColor" stroke="currentColor" strokeWidth="0.6"/>
  </svg>
);

const FILTER_OPTIONS_DEBOUNCE_MS = 300;
const FILTER_OPTIONS_LIMIT = 50;

const ClientsFilterDropdown = ({
  title,
  field,
  filters,
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  visible,
  staticOptions = null,
}) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [remoteItems, setRemoteItems] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    if (visible !== true) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, FILTER_OPTIONS_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search, visible]);

  useEffect(() => {
    if (visible === true) {
      setSearch('');
      setDebouncedSearch('');
    }
  }, [visible]);

  useEffect(() => {
    if (visible !== true || staticOptions || !field) {
      return undefined;
    }
    let cancelled = false;
    const handleLoadOptions = async () => {
      setLoadingOptions(true);
      try {
        const response = await apiService.getClientFilterOptions({
          field,
          q: debouncedSearch,
          limit: FILTER_OPTIONS_LIMIT,
        });
        if (cancelled) {
          return;
        }
        const nextItems = Array.isArray(response.data?.items) ? response.data.items : [];
        setRemoteItems(nextItems);
      } catch (error) {
        if (!cancelled) {
          setRemoteItems([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingOptions(false);
        }
      }
    };
    handleLoadOptions();
    return () => {
      cancelled = true;
    };
  }, [visible, field, debouncedSearch, staticOptions]);

  const items = staticOptions || filters || remoteItems;
  const selected = Array.isArray(selectedKeys) ? selectedKeys : [];
  const mergedItems = [
    ...items,
    ...selected
      .filter((key) => !items.some((item) => String(item.value) === String(key)))
      .map((key) => ({
        text: String(key) === '' || String(key) === '__empty__' ? '—' : String(key),
        value: key,
      })),
  ];
  const filtered = search && staticOptions
    ? mergedItems.filter((f) => String(f.text).toLowerCase().includes(search.toLowerCase()))
    : mergedItems;

  const handleToggle = (val) => {
    const next = selectedKeys.some((k) => String(k) === String(val))
      ? selectedKeys.filter((k) => String(k) !== String(val))
      : [...selectedKeys, val];
    setSelectedKeys(next);
  };

  const handleReset = () => {
    setSelectedKeys([]);
    clearFilters && clearFilters();
    confirm({ closeDropdown: true });
  };

  const handleApply = () => {
    confirm({ closeDropdown: true });
  };

  return (
    <div className="cl-filter-dropdown">
      <div className="cl-filter-dropdown__header">Фильтр: {title}</div>
      <div className="cl-filter-dropdown__search">
        <input
          className="cl-filter-dropdown__search-input"
          placeholder="Поиск..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </div>
      <div className="cl-filter-dropdown__list">
        {loadingOptions && (
          <div className="cl-filter-dropdown__empty">Загрузка...</div>
        )}
        {!loadingOptions && filtered.length === 0 && (
          <div className="cl-filter-dropdown__empty">Ничего не найдено</div>
        )}
        {filtered.map((f) => (
          <label key={String(f.value)} className="cl-filter-dropdown__item">
            <input
              type="checkbox"
              className="cl-filter-dropdown__checkbox"
              checked={selectedKeys.some((k) => String(k) === String(f.value))}
              onChange={() => handleToggle(f.value)}
            />
            <span className="cl-filter-dropdown__label">{f.text}</span>
          </label>
        ))}
      </div>
      <div className="cl-filter-dropdown__actions">
        <button type="button" className="cl-filter-dropdown__btn cl-filter-dropdown__btn--reset" onClick={handleReset}>
          Сбросить
        </button>
        <button type="button" className="cl-filter-dropdown__btn cl-filter-dropdown__btn--apply" onClick={handleApply}>
          Применить
        </button>
      </div>
    </div>
  );
};

const ClientsCreatedAtRangeFilterDropdown = ({
  visible,
  from,
  to,
  onApplyRange,
  confirm,
  clearFilters,
  setSelectedKeys,
}) => {
  const [range, setRange] = useState(() => [from ? dayjs(from) : null, to ? dayjs(to) : null]);

  useEffect(() => {
    if (visible !== true) {
      return;
    }
    setRange([from ? dayjs(from) : null, to ? dayjs(to) : null]);
  }, [visible, from, to]);

  const pickerValue =
    range && (range[0] || range[1]) ? [range[0] ?? null, range[1] ?? null] : null;

  const handleReset = () => {
    setRange([null, null]);
    onApplyRange(null, null);
    setSelectedKeys?.([]);
    clearFilters?.();
    confirm?.({ closeDropdown: true });
  };

  const handleApply = () => {
    const start = range?.[0];
    const end = range?.[1];
    const fromStr = start ? start.format('YYYY-MM-DD') : null;
    const toStr = end ? end.format('YYYY-MM-DD') : null;
    onApplyRange(fromStr, toStr);
    setSelectedKeys?.(fromStr || toStr ? ['__created_range__'] : []);
    confirm?.({ closeDropdown: true });
  };

  return (
    <div className="cl-filter-dropdown cl-filter-dropdown--range">
      <div className="cl-filter-dropdown__header">Фильтр: Дата создания</div>
      <div className="cl-filter-dropdown__range">
        <DatePicker.RangePicker
          value={pickerValue}
          onChange={(next) => setRange(next || [null, null])}
          format="DD.MM.YYYY"
          allowEmpty={[true, true]}
          placeholder={['С даты', 'По дату']}
          className="cl-filter-dropdown__range-picker"
          getPopupContainer={(trigger) => trigger?.parentElement ?? document.body}
        />
        <p className="cl-filter-dropdown__range-hint">Запись без даты создания в диапазон не попадает</p>
      </div>
      <div className="cl-filter-dropdown__actions">
        <button type="button" className="cl-filter-dropdown__btn cl-filter-dropdown__btn--reset" onClick={handleReset}>
          Сбросить
        </button>
        <button type="button" className="cl-filter-dropdown__btn cl-filter-dropdown__btn--apply" onClick={handleApply}>
          Применить
        </button>
      </div>
    </div>
  );
};

const parseOptionalFilterNumber = (raw) => {
  if (raw == null || String(raw).trim() === '') {
    return null;
  }
  const n = Number(String(raw).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const normalizeMinMaxPair = (min, max) => {
  if (min != null && max != null && min > max) {
    return { min: max, max: min };
  }
  return { min, max };
};

const ClientsNumericRangeFilterDropdown = ({
  visible,
  title,
  minVal,
  maxVal,
  onApplyRange,
  confirm,
  clearFilters,
  setSelectedKeys,
  selectedKeyToken,
  minPlaceholder = 'От',
  maxPlaceholder = 'До',
  inputStep = '1',
  hint = null,
}) => {
  const [minStr, setMinStr] = useState('');
  const [maxStr, setMaxStr] = useState('');

  useEffect(() => {
    if (visible !== true) {
      return;
    }
    setMinStr(minVal != null && Number.isFinite(minVal) ? String(minVal) : '');
    setMaxStr(maxVal != null && Number.isFinite(maxVal) ? String(maxVal) : '');
  }, [visible, minVal, maxVal]);

  const handleReset = () => {
    setMinStr('');
    setMaxStr('');
    onApplyRange(null, null);
    setSelectedKeys?.([]);
    clearFilters?.();
    confirm?.({ closeDropdown: true });
  };

  const handleApply = () => {
    let min = parseOptionalFilterNumber(minStr);
    let max = parseOptionalFilterNumber(maxStr);
    const norm = normalizeMinMaxPair(min, max);
    min = norm.min;
    max = norm.max;
    onApplyRange(min, max);
    setSelectedKeys?.(min != null || max != null ? [selectedKeyToken] : []);
    confirm?.({ closeDropdown: true });
  };

  return (
    <div className="cl-filter-dropdown cl-filter-dropdown--range cl-filter-dropdown--numeric-range">
      <div className="cl-filter-dropdown__header">Фильтр: {title}</div>
      <div className="cl-filter-dropdown__numeric-range-body">
        <label className="cl-filter-dropdown__num-field">
          <span className="cl-filter-dropdown__num-label">{minPlaceholder}</span>
          <input
            type="number"
            className="cl-filter-dropdown__num-input"
            step={inputStep}
            value={minStr}
            onChange={(e) => setMinStr(e.target.value)}
            aria-label={`${title}: минимум`}
            tabIndex={0}
          />
        </label>
        <label className="cl-filter-dropdown__num-field">
          <span className="cl-filter-dropdown__num-label">{maxPlaceholder}</span>
          <input
            type="number"
            className="cl-filter-dropdown__num-input"
            step={inputStep}
            value={maxStr}
            onChange={(e) => setMaxStr(e.target.value)}
            aria-label={`${title}: максимум`}
            tabIndex={0}
          />
        </label>
        {hint ? <p className="cl-filter-dropdown__range-hint">{hint}</p> : null}
      </div>
      <div className="cl-filter-dropdown__actions">
        <button type="button" className="cl-filter-dropdown__btn cl-filter-dropdown__btn--reset" onClick={handleReset}>
          Сбросить
        </button>
        <button type="button" className="cl-filter-dropdown__btn cl-filter-dropdown__btn--apply" onClick={handleApply}>
          Применить
        </button>
      </div>
    </div>
  );
};

/* ============ PAGINATION ICONS ============ */
const ClChevronDoubleLeft = () => (
  <svg className="cl-pagination__btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 17L6 12L11 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18 17L13 12L18 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ClChevronLeft = () => (
  <svg className="cl-pagination__btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ClChevronRight = () => (
  <svg className="cl-pagination__btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ClChevronDoubleRight = () => (
  <svg className="cl-pagination__btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 7L18 12L13 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6 7L11 12L6 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ClChevronDown = () => (
  <svg className="cl-pagination__select-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ============ PER PAGE DROPDOWN ============ */
const ClPerPageDropdown = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="cl-pagination__perpage" ref={ref}>
      <button
        type="button"
        className="cl-pagination__select"
        onClick={() => setOpen(!open)}
        aria-label={`${value} на странице`}
        aria-expanded={open}
        tabIndex={0}
      >
        <span className="cl-pagination__select-value">{value}</span>
        <span className="cl-pagination__select-text"> на странице</span>
        <ClChevronDown />
      </button>
      {open && (
        <div className="cl-pagination__dropdown">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              className={`cl-pagination__option ${opt === value ? 'cl-pagination__option--active' : ''}`}
              onClick={() => { onChange(opt); setOpen(false); }}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const formatRubInt = (value) =>
  new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(Math.round(Number(value)));

const getPrivilegeLevelModifier = (raw) => {
  const lower = String(raw || '').trim().toLowerCase();
  if (!lower) {
    return '';
  }
  if (lower.includes('бронз') || lower === 'bronze') {
    return 'clients-level--bronze';
  }
  if (lower.includes('серебр') || lower === 'silver') {
    return 'clients-level--silver';
  }
  if (lower.includes('золот') || lower === 'gold') {
    return 'clients-level--gold';
  }
  return '';
};

const renderPrivilegeLevel = (raw) => {
  const t = raw && String(raw).trim();
  if (!t) {
    return <span className="clients-level clients-level--empty">—</span>;
  }
  const mod = getPrivilegeLevelModifier(t);
  return <span className={`clients-level ${mod}`.trim()}>{t}</span>;
};

const CLIENTS_DEFAULT_SORT_CONFIG = { field: 'proxy_count', order: 'descend' };
const CLIENT_STATUS_FILTER_OPTIONS = [
  { text: 'Активен', value: 'active' },
  { text: 'Неактивен', value: 'inactive' },
];
const CLIENT_FORBIDDEN_FILTER_OPTIONS = [
  { text: 'Вкл', value: 'on' },
  { text: 'Выкл', value: 'off' },
];
const CLIENT_PRIVILEGE_FILTER_OPTIONS = [
  { text: 'Бронза', value: 'Бронза' },
  { text: 'Серебро', value: 'Серебро' },
  { text: 'Золото', value: 'Золото' },
  { text: '—', value: '__empty__' },
];

const getClientKrakenDisplay = (c) => c.kraken_proxy_auth_login || c.login || '';

/** Все идентификаторы клиента для deep-link из прокси (client_login) и фильтра таблицы. */
const getClientLoginIdentifiers = (c) => {
  if (!c) {
    return [];
  }
  return [c.login, c.kraken_proxy_auth_login, c.cabinet_login]
    .filter((value) => value != null && String(value).trim() !== '')
    .map((value) => String(value).trim());
};

const clientMatchesLoginParam = (client, loginParam) => {
  if (!client || loginParam == null || String(loginParam).trim() === '') {
    return false;
  }
  const needle = String(loginParam).trim();
  return getClientLoginIdentifiers(client).some((identifier) => identifier === needle);
};

const getClientActiveProxyCount = (c) => {
  const raw = c?.active_proxy_count ?? c?.activeProxyCount;
  if (raw == null || raw === '') {
    return 0;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

const normalizeClientListRow = (client) => ({
  ...client,
  active_proxy_count: getClientActiveProxyCount(client),
});
const getClientEmailDisplay = (c) => normalizeClientCabinetEmailDisplay(c?.cabinet_email ?? c?.email ?? '');

const formatPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return '—';
  }
  return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(n)}%`;
};

const Clients = () => {
  const [searchParams] = useSearchParams();
  const clientLoginFromUrl = useMemo(() => {
    const raw = searchParams.get('client_login');
    if (raw == null || String(raw).trim() === '') {
      return '';
    }
    return String(raw).trim();
  }, [searchParams]);
  const clientIdFromUrl = useMemo(() => {
    const raw = searchParams.get('client_id');
    if (raw == null || String(raw).trim() === '') {
      return null;
    }
    const parsed = Number(String(raw).trim());
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return null;
    }
    return parsed;
  }, [searchParams]);
  const clientDeepLinkKey = useMemo(() => {
    if (clientIdFromUrl != null) {
      return `id:${clientIdFromUrl}`;
    }
    if (clientLoginFromUrl) {
      return `login:${clientLoginFromUrl}`;
    }
    return '';
  }, [clientIdFromUrl, clientLoginFromUrl]);
  const [clients, setClients] = useState([]);
  const [clientsMeta, setClientsMeta] = useState({
    total: 0,
    active_count: 0,
    inactive_count: 0,
    has_active_proxies_count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [loginFilter, setLoginFilter] = useState([]);
  const effectiveLoginFilter = useMemo(() => {
    if (loginFilter.length > 0) {
      return loginFilter;
    }
    if (clientLoginFromUrl) {
      return [clientLoginFromUrl];
    }
    return [];
  }, [loginFilter, clientLoginFromUrl]);
  const [emailFilter, setEmailFilter] = useState([]);
  const [idFilter, setIdFilter] = useState([]);
  const [cabinetLoginFilter, setCabinetLoginFilter] = useState([]);
  const [statusColumnFilter, setStatusColumnFilter] = useState([]);
  const [skipForbiddenFilter, setSkipForbiddenFilter] = useState([]);
  const [proxyCountMin, setProxyCountMin] = useState(null);
  const [proxyCountMax, setProxyCountMax] = useState(null);
  const [createdAtFrom, setCreatedAtFrom] = useState(null);
  const [createdAtTo, setCreatedAtTo] = useState(null);
  const [privilegeLevelFilter, setPrivilegeLevelFilter] = useState([]);
  const [ordersCountMin, setOrdersCountMin] = useState(null);
  const [ordersCountMax, setOrdersCountMax] = useState(null);
  const [balanceRubMin, setBalanceRubMin] = useState(null);
  const [balanceRubMax, setBalanceRubMax] = useState(null);
  const [rolling30RubMin, setRolling30RubMin] = useState(null);
  const [rolling30RubMax, setRolling30RubMax] = useState(null);
  const [lifetimeRubMin, setLifetimeRubMin] = useState(null);
  const [lifetimeRubMax, setLifetimeRubMax] = useState(null);
  const [salesRubMin, setSalesRubMin] = useState(null);
  const [salesRubMax, setSalesRubMax] = useState(null);
  const [discountPercentMin, setDiscountPercentMin] = useState(null);
  const [discountPercentMax, setDiscountPercentMax] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'active' | 'inactive'
  const [hasActiveProxiesFilter, setHasActiveProxiesFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [sortConfig, setSortConfig] = useState(CLIENTS_DEFAULT_SORT_CONFIG);
  const [sortByNewest, setSortByNewest] = useState(false);
  const sortConfigBeforeNewestRef = useRef(null);
  const perPageOptions = [5, 10, 20, 50];
  const clientFilterCount = useMemo(() => {
    let c = 0;
    if (loginFilter.length > 0) c++;
    if (emailFilter.length > 0) c++;
    if (idFilter.length > 0) c++;
    if (cabinetLoginFilter.length > 0) c++;
    if (statusColumnFilter.length > 0) c++;
    if (skipForbiddenFilter.length > 0) c++;
    if (proxyCountMin != null || proxyCountMax != null) c++;
    if (createdAtFrom || createdAtTo) c++;
    if (privilegeLevelFilter.length > 0) c++;
    if (ordersCountMin != null || ordersCountMax != null) c++;
    if (balanceRubMin != null || balanceRubMax != null) c++;
    if (rolling30RubMin != null || rolling30RubMax != null) c++;
    if (lifetimeRubMin != null || lifetimeRubMax != null) c++;
    if (salesRubMin != null || salesRubMax != null) c++;
    if (discountPercentMin != null || discountPercentMax != null) c++;
    if (activeFilter !== 'all') c++;
    if (hasActiveProxiesFilter) c++;
    return c;
  }, [
    loginFilter,
    emailFilter,
    idFilter,
    cabinetLoginFilter,
    statusColumnFilter,
    skipForbiddenFilter,
    proxyCountMin,
    proxyCountMax,
    createdAtFrom,
    createdAtTo,
    privilegeLevelFilter,
    ordersCountMin,
    ordersCountMax,
    balanceRubMin,
    balanceRubMax,
    rolling30RubMin,
    rolling30RubMax,
    lifetimeRubMin,
    lifetimeRubMax,
    salesRubMin,
    salesRubMax,
    discountPercentMin,
    discountPercentMax,
    activeFilter,
    hasActiveProxiesFilter,
  ]);
  const handleClearAllClientFilters = useCallback(() => {
    setLoginFilter([]);
    setEmailFilter([]);
    setIdFilter([]);
    setCabinetLoginFilter([]);
    setStatusColumnFilter([]);
    setSkipForbiddenFilter([]);
    setProxyCountMin(null);
    setProxyCountMax(null);
    setCreatedAtFrom(null);
    setCreatedAtTo(null);
    setPrivilegeLevelFilter([]);
    setOrdersCountMin(null);
    setOrdersCountMax(null);
    setBalanceRubMin(null);
    setBalanceRubMax(null);
    setRolling30RubMin(null);
    setRolling30RubMax(null);
    setLifetimeRubMin(null);
    setLifetimeRubMax(null);
    setSalesRubMin(null);
    setSalesRubMax(null);
    setDiscountPercentMin(null);
    setDiscountPercentMax(null);
    setActiveFilter('all');
    setHasActiveProxiesFilter(false);
    setCurrentPage(1);
  }, []);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkStatusUpdating, setBulkStatusUpdating] = useState(false);
  const [bulkEditVisible, setBulkEditVisible] = useState(false);
  const [bulkEditing, setBulkEditing] = useState(false);
  const [detailClient, setDetailClient] = useState(null);
  const lastSelectedClientKeyRef = useRef(null);
  const prevClientLoginParamRef = useRef(null);
  const fetchClientsSeqRef = useRef(0);

  const resolveClientFromDeepLink = useCallback(async () => {
    if (clientIdFromUrl != null) {
      const response = await apiService.getClient(clientIdFromUrl);
      return normalizeClientListRow(response.data);
    }
    if (!clientLoginFromUrl) {
      return null;
    }

    const localMatch = clients.find((client) => clientMatchesLoginParam(client, clientLoginFromUrl));
    if (localMatch) {
      return localMatch;
    }

    const response = await apiService.getClients({
      login: clientLoginFromUrl,
      limit: 50,
      offset: 0,
    });
    const rawClients = Array.isArray(response.data?.data)
      ? response.data.data
      : (Array.isArray(response.data) ? response.data : []);
    return rawClients
      .map(normalizeClientListRow)
      .find((client) => clientMatchesLoginParam(client, clientLoginFromUrl)) || null;
  }, [clientIdFromUrl, clientLoginFromUrl, clients]);

  const totalCount = Number(clientsMeta.total ?? 0);
  const activeCount = Number(clientsMeta.active_count ?? 0);
  const inactiveCount = Number(clientsMeta.inactive_count ?? 0);
  const hasActiveProxiesCount = Number(clientsMeta.has_active_proxies_count ?? 0);

  const handleToggleHasActiveProxiesFilter = useCallback(() => {
    setHasActiveProxiesFilter((prev) => !prev);
    setCurrentPage(1);
  }, []);

  const filteredClients = clients;
  const paginatedClients = clients;

  const [submitting, setSubmitting] = useState(false);
  const handleToggleSortByNewest = useCallback(() => {
    if (sortByNewest) {
      setSortByNewest(false);
      setSortConfig(sortConfigBeforeNewestRef.current || CLIENTS_DEFAULT_SORT_CONFIG);
      sortConfigBeforeNewestRef.current = null;
    } else {
      sortConfigBeforeNewestRef.current = sortConfig;
      setSortByNewest(true);
      setSortConfig({ field: 'created_at', order: 'descend' });
    }
    setCurrentPage(1);
  }, [sortByNewest, sortConfig]);

  const handleClearAllClientFiltersWithSort = useCallback(() => {
    handleClearAllClientFilters();
    setSortByNewest(false);
    sortConfigBeforeNewestRef.current = null;
    setSortConfig(CLIENTS_DEFAULT_SORT_CONFIG);
  }, [handleClearAllClientFilters]);

  const totalItems = Number(clientsMeta.total ?? clients.length);
  const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
  const rangeStart = totalItems === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const rangeEnd = Math.min(currentPage * perPage, totalItems);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const fetchClients = useCallback(async () => {
    const requestSeq = fetchClientsSeqRef.current + 1;
    fetchClientsSeqRef.current = requestSeq;
    try {
      setLoading(true);
      const rawParams = {
        limit: perPage,
        offset: (currentPage - 1) * perPage,
        logins: effectiveLoginFilter.length ? effectiveLoginFilter : undefined,
        emails: emailFilter.length ? emailFilter : undefined,
        ids: idFilter.length ? idFilter : undefined,
        cabinet_logins: cabinetLoginFilter.length ? cabinetLoginFilter : undefined,
        status: statusColumnFilter.length ? statusColumnFilter : undefined,
        skip_forbidden: skipForbiddenFilter.length ? skipForbiddenFilter : undefined,
        privilege_level: privilegeLevelFilter.length ? privilegeLevelFilter : undefined,
        proxy_count_min: proxyCountMin,
        proxy_count_max: proxyCountMax,
        created_at_from: createdAtFrom || undefined,
        created_at_to: createdAtTo || undefined,
        orders_count_min: ordersCountMin,
        orders_count_max: ordersCountMax,
        balance_rub_min: balanceRubMin,
        balance_rub_max: balanceRubMax,
        rolling_30d_rub_min: rolling30RubMin,
        rolling_30d_rub_max: rolling30RubMax,
        lifetime_rub_min: lifetimeRubMin,
        lifetime_rub_max: lifetimeRubMax,
        sales_rub_min: salesRubMin,
        sales_rub_max: salesRubMax,
        discount_percent_min: discountPercentMin,
        discount_percent_max: discountPercentMax,
        has_active_proxies: hasActiveProxiesFilter ? true : undefined,
        is_active:
          activeFilter === 'active'
            ? true
            : activeFilter === 'inactive'
              ? false
              : undefined,
        sort_by: sortConfig?.field || CLIENTS_DEFAULT_SORT_CONFIG.field,
        sort_order: sortConfig?.order === 'ascend' ? 'asc' : 'desc',
      };
      const response = await apiService.getClients(
        Object.fromEntries(Object.entries(rawParams).filter(([, value]) => value != null))
      );
      if (requestSeq !== fetchClientsSeqRef.current) {
        return;
      }
      const rawClients = Array.isArray(response.data?.data) ? response.data.data : [];
      const nextClients = rawClients.map(normalizeClientListRow);
      const nextMeta = response.data?.meta || {};

      setClients(nextClients);
      setClientsMeta({
        total: Number(nextMeta.total ?? nextClients.length),
        active_count: Number(nextMeta.active_count ?? nextClients.filter((client) => client.is_active).length),
        inactive_count: Number(nextMeta.inactive_count ?? nextClients.filter((client) => !client.is_active).length),
        has_active_proxies_count: Number(
          nextMeta.has_active_proxies_count
            ?? nextClients.filter((client) => getClientActiveProxyCount(client) > 0).length
        ),
      });
      setSelectedClientIds((prev) => {
        const availableIds = new Set(nextClients.map((client) => client.id));
        return prev.filter((id) => availableIds.has(id));
      });
    } catch (error) {
      if (requestSeq === fetchClientsSeqRef.current) {
        message.error('Ошибка загрузки клиентов');
      }
    } finally {
      if (requestSeq === fetchClientsSeqRef.current) {
        setLoading(false);
      }
    }
  }, [
    perPage,
    currentPage,
    activeFilter,
    hasActiveProxiesFilter,
    effectiveLoginFilter,
    emailFilter,
    idFilter,
    cabinetLoginFilter,
    statusColumnFilter,
    skipForbiddenFilter,
    privilegeLevelFilter,
    proxyCountMin,
    proxyCountMax,
    createdAtFrom,
    createdAtTo,
    ordersCountMin,
    ordersCountMax,
    balanceRubMin,
    balanceRubMax,
    rolling30RubMin,
    rolling30RubMax,
    lifetimeRubMin,
    lifetimeRubMax,
    salesRubMin,
    salesRubMax,
    discountPercentMin,
    discountPercentMax,
    sortConfig,
  ]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    if (!clientLoginFromUrl) {
      return;
    }
    setLoginFilter([clientLoginFromUrl]);
    setCurrentPage(1);
  }, [clientLoginFromUrl]);

  useEffect(() => {
    if (!clientDeepLinkKey) {
      prevClientLoginParamRef.current = null;
      return;
    }
    if (loading) {
      return;
    }

    let cancelled = false;
    const paramChanged = prevClientLoginParamRef.current !== clientDeepLinkKey;

    const handleOpenDeepLinkedClient = async () => {
      try {
        const match = await resolveClientFromDeepLink();
        if (cancelled) {
          return;
        }
        if (!match) {
          if (paramChanged) {
            message.warning(`Клиент «${clientLoginFromUrl || clientIdFromUrl}» не найден в списке`);
            prevClientLoginParamRef.current = clientDeepLinkKey;
          }
          return;
        }

        if (clientLoginFromUrl) {
          setLoginFilter([clientLoginFromUrl]);
        }
        setClients((prev) => {
          if (prev.some((client) => client.id === match.id)) {
            return prev;
          }
          return [...prev, match];
        });
        if (paramChanged) {
          setDetailClient(match);
          prevClientLoginParamRef.current = clientDeepLinkKey;
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (paramChanged) {
          message.warning(`Клиент «${clientLoginFromUrl || clientIdFromUrl}» не найден в списке`);
          prevClientLoginParamRef.current = clientDeepLinkKey;
        }
      }
    };

    handleOpenDeepLinkedClient();
    return () => {
      cancelled = true;
    };
  }, [clientDeepLinkKey, clientLoginFromUrl, clientIdFromUrl, loading, resolveClientFromDeepLink]);

  useEffect(() => {
    setDetailClient((prev) => {
      if (!prev) {
        return null;
      }
      const fresh = clients.find((c) => c.id === prev.id);
      if (!fresh) {
        return prev;
      }
      // Не затираем поля дроуера (напр. privilege_* и профиль ЛК), если в ответе списка их нет/пусто.
      return mergeDetailClientWithListRow(prev, fresh);
    });
  }, [clients]);

  const handleCreateClient = async (values) => {
    try {
      setSubmitting(true);
      if (editingClient) {
        // Обновление существующего клиента
        const updateData = {};
        if (values.login !== editingClient.login) updateData.login = values.login;
        if (values.email !== editingClient.email) updateData.email = values.email;
        if (values.password && values.password.trim()) updateData.password = values.password;
        
        await apiService.updateClient(editingClient.id, updateData);
        message.success('Клиент успешно обновлен');
      } else {
        // Создание нового клиента
        await apiService.createClient(values);
        message.success('Клиент успешно создан');
      }
      setModalVisible(false);
      setEditingClient(null);
      fetchClients({ force: true });
    } catch (error) {
      message.error(error.response?.data?.detail || (editingClient ? 'Ошибка обновления клиента' : 'Ошибка создания клиента'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setModalVisible(true);
  };

  const handleClientUpdatedFromDrawer = useCallback((patch) => {
    if (!patch || patch.id == null) {
      return;
    }
    const id = patch.id;
    const balanceRub = patch.balance_rub ?? patch.cabinet_balance;
    const merged = { ...patch, balance_rub: balanceRub, cabinet_balance: patch.cabinet_balance ?? balanceRub };
    setDetailClient((prev) => (prev && prev.id === id ? { ...prev, ...merged } : prev));
    setClients((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...merged } : c)),
    );
  }, []);

  const handleDeleteClient = async (clientId) => {
    try {
      // Проверяем, существует ли клиент в текущем списке
      const clientExists = clients.find(client => client.id === clientId);
      if (!clientExists) {
        message.warning('Клиент не найден в текущем списке. Обновляю данные...');
        await fetchClients({ force: true });
        return;
      }

      await apiService.deleteClient(clientId);
      message.success('Клиент и все связанные прокси удалены (каскадное удаление)');
      
      // Принудительно обновляем список клиентов
      await fetchClients({ force: true });
      
      // Дополнительная проверка - если список не обновился, показываем предупреждение
      setTimeout(async () => {
        await fetchClients({ force: true });
      }, 1000);
      
    } catch (error) {
      if (error.response?.status === 404) {
        message.error('Клиент не найден. Возможно, он уже был удален. Обновляю список...');
        await fetchClients({ force: true }); // Обновляем список после 404 ошибки
      } else {
        message.error(error.response?.data?.detail || 'Ошибка удаления клиента');
        // Даже при ошибке обновляем список
        await fetchClients({ force: true });
      }
    }
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      setBulkDeleting(true);
      const idsToDelete = selectedClientIds.map((id) => Number(id));
      if (!idsToDelete.length) {
        message.warning('Для массового удаления сначала выберите клиентов');
        return;
      }

      const response = await apiService.bulkDeleteClients(idsToDelete);
      const data = response.data || {};
      const deletedCount = data.deleted_count || 0;
      const notFoundIds = data.not_found_ids || [];
      const errors = data.errors || [];

      if (deletedCount > 0) {
        message.success(`Удалено клиентов: ${deletedCount}`);
      } else if (!errors.length && !notFoundIds.length) {
        message.info('Клиенты для удаления не найдены');
      }

      if (notFoundIds.length > 0) {
        message.warning(`Не найдены клиенты: ${notFoundIds.join(', ')}`);
      }

      if (errors.length > 0) {
        const preview = errors
          .slice(0, 3)
          .map((item) => {
            const idPart = item?.client_id != null ? `#${item.client_id}` : '';
            const messagePart = item?.error || 'неизвестная ошибка';
            return `${idPart}: ${messagePart}`.trim();
          })
          .join('; ');
        const suffix = errors.length > 3 ? ` (+${errors.length - 3})` : '';
        message.error(`Ошибки при удалении клиентов: ${errors.length}. ${preview}${suffix}`);
      }

      await fetchClients({ force: true });
      setSelectedClientIds([]);
    } catch (error) {
      message.error(error.response?.data?.detail || 'Ошибка массового удаления клиентов');
    } finally {
      setBulkDeleting(false);
    }
  };

  const showBulkDeleteConfirm = () => {
    if (!selectedClientIds.length) {
      return;
    }

    Modal.confirm({
      title: `Удалить выбранных клиентов (${selectedClientIds.length})?`,
      content: 'ВНИМАНИЕ: Будет выполнено каскадное удаление — клиенты и ВСЕ их прокси будут удалены безвозвратно.',
      okText: 'Да, удалить',
      cancelText: 'Отмена',
      okType: 'danger',
      onOk: () => handleBulkDeleteConfirm(),
    });
  };

  const selectedActiveClientIds = useMemo(() => {
    const selectedSet = new Set(selectedClientIds.map((id) => String(id)));
    return (clients || [])
      .filter((client) => selectedSet.has(String(client.id)) && client.is_active)
      .map((client) => Number(client.id));
  }, [clients, selectedClientIds]);

  const selectedInactiveClientIds = useMemo(() => {
    const selectedSet = new Set(selectedClientIds.map((id) => String(id)));
    return (clients || [])
      .filter((client) => selectedSet.has(String(client.id)) && !client.is_active)
      .map((client) => Number(client.id));
  }, [clients, selectedClientIds]);

  const selectedBulkStatusAction = useMemo(() => {
    const enableCount = selectedInactiveClientIds.length;
    const disableCount = selectedActiveClientIds.length;
    if (enableCount > 0 && disableCount > 0) {
      return 'mixed';
    }
    if (enableCount > 0) {
      return 'enable';
    }
    if (disableCount > 0) {
      return 'disable';
    }
    return null;
  }, [selectedActiveClientIds, selectedInactiveClientIds]);

  const selectedClientsForBulk = useMemo(() => {
    const selectedSet = new Set(selectedClientIds.map((id) => Number(id)));
    return (clients || []).filter((client) => selectedSet.has(Number(client.id)));
  }, [clients, selectedClientIds]);

  useEffect(() => {
    if (bulkEditVisible && selectedClientIds.length === 0) {
      setBulkEditVisible(false);
    }
  }, [bulkEditVisible, selectedClientIds.length]);

  const handleOpenBulkEdit = useCallback(() => {
    if (!selectedClientIds.length) {
      return;
    }
    setBulkEditVisible(true);
  }, [selectedClientIds.length]);

  const handleCloseBulkEdit = useCallback(() => {
    if (bulkEditing) {
      return;
    }
    setBulkEditVisible(false);
  }, [bulkEditing]);

  const handleBulkEditSubmit = useCallback(async (payload) => {
    const idsToUpdate = selectedClientIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
    if (!idsToUpdate.length) {
      message.warning('Для массового редактирования сначала выберите клиентов');
      return;
    }
    if (!payload || !Object.keys(payload).length) {
      message.warning('Измените хотя бы одно поле');
      return;
    }

    try {
      setBulkEditing(true);
      const response = await apiService.bulkUpdateClients(idsToUpdate, payload);
      const data = response.data || {};
      const updatedCount = data.updated_count || 0;
      const notFoundIds = data.not_found_ids || [];
      const errors = data.errors || [];

      if (updatedCount > 0) {
        const privilegeLabels = { bronze: 'Бронза', silver: 'Серебро', gold: 'Золото' };
        const listPatch = { ...payload };
        if (Object.prototype.hasOwnProperty.call(payload, 'privilege_tier_override')) {
          const raw = payload.privilege_tier_override;
          const normalized = raw && String(raw).trim() ? String(raw).trim().toLowerCase() : null;
          listPatch.privilege_tier_override = normalized;
          if (normalized && privilegeLabels[normalized]) {
            listPatch.privilege_level = privilegeLabels[normalized];
          }
        }
        const updatedSet = new Set((data.updated_client_ids || idsToUpdate).map((id) => Number(id)));
        setClients((prev) => prev.map((c) => (
          updatedSet.has(Number(c.id)) ? { ...c, ...listPatch } : c
        )));
        setDetailClient((prev) => (
          prev && updatedSet.has(Number(prev.id)) ? { ...prev, ...listPatch } : prev
        ));
        message.success(`Обновлено клиентов: ${updatedCount}`);
      } else if (!errors.length && !notFoundIds.length) {
        message.info('Клиенты для обновления не найдены');
      }

      if (notFoundIds.length > 0) {
        message.warning(`Не найдены клиенты: ${notFoundIds.join(', ')}`);
      }

      if (errors.length > 0) {
        const preview = errors
          .slice(0, 3)
          .map((item) => {
            const idPart = item?.client_id != null ? `#${item.client_id}` : '';
            const messagePart = item?.error || 'неизвестная ошибка';
            return `${idPart}: ${messagePart}`.trim();
          })
          .join('; ');
        const suffix = errors.length > 3 ? ` (+${errors.length - 3})` : '';
        message.error(`Ошибки при обновлении клиентов: ${errors.length}. ${preview}${suffix}`);
      }

      await fetchClients({ force: true });
      setBulkEditVisible(false);
    } catch (error) {
      message.error(error.response?.data?.detail || 'Ошибка массового редактирования клиентов');
    } finally {
      setBulkEditing(false);
    }
  }, [selectedClientIds, fetchClients]);

  const reportBulkStatusResponse = (data, mode) => {
    const enabledCount = data.enabled_count || 0;
    const disabledCount = data.disabled_count || 0;
    const skippedCount = data.skipped_count || 0;
    const notFoundIds = data.not_found_ids || [];
    const errors = data.errors || [];
    const proxiesAffectedTotal = data.proxies_affected_total || 0;
    const changedCount = mode === 'enable' ? enabledCount : disabledCount;
    const actionLabel = mode === 'enable' ? 'Включено' : 'Отключено';

    if (changedCount > 0) {
      message.success(`${actionLabel} клиентов: ${changedCount}. Затронуто прокси: ${proxiesAffectedTotal}`);
    } else if (!errors.length && !notFoundIds.length) {
      message.info(
        mode === 'enable'
          ? 'Неактивные клиенты для включения не найдены'
          : 'Активные клиенты для отключения не найдены',
      );
    }

    if (skippedCount > 0) {
      message.info(
        mode === 'enable'
          ? `Уже были включены: ${skippedCount}`
          : `Уже были отключены: ${skippedCount}`,
      );
    }

    if (notFoundIds.length > 0) {
      message.warning(`Не найдены клиенты: ${notFoundIds.join(', ')}`);
    }

    if (errors.length > 0) {
      message.error(
        mode === 'enable'
          ? `Ошибки при включении клиентов: ${errors.length}`
          : `Ошибки при отключении клиентов: ${errors.length}`,
      );
    }
  };

  const handleBulkStatusConfirm = async () => {
    const idsToEnable = selectedInactiveClientIds;
    const idsToDisable = selectedActiveClientIds;
    if (!idsToEnable.length && !idsToDisable.length) {
      message.warning('Среди выбранных нет клиентов для изменения статуса');
      return;
    }

    try {
      setBulkStatusUpdating(true);
      if (idsToEnable.length) {
        const response = await apiService.bulkEnableClients(idsToEnable);
        reportBulkStatusResponse(response.data || {}, 'enable');
      }
      if (idsToDisable.length) {
        const response = await apiService.bulkDisableClients(idsToDisable);
        reportBulkStatusResponse(response.data || {}, 'disable');
      }
      await fetchClients({ force: true });
      setSelectedClientIds([]);
    } catch (error) {
      message.error(error.response?.data?.detail || 'Ошибка изменения статуса выбранных клиентов');
    } finally {
      setBulkStatusUpdating(false);
    }
  };

  const showBulkStatusConfirm = () => {
    if (!selectedClientIds.length || !selectedBulkStatusAction) {
      return;
    }

    const enableCount = selectedInactiveClientIds.length;
    const disableCount = selectedActiveClientIds.length;

    if (selectedBulkStatusAction === 'enable') {
      Modal.confirm({
        title: `Включить выбранных клиентов (${enableCount})?`,
        content: 'Будут включены все прокси выбранных неактивных клиентов. Продолжить?',
        okText: 'Включить',
        cancelText: 'Отмена',
        okType: 'primary',
        onOk: () => handleBulkStatusConfirm(),
      });
      return;
    }

    if (selectedBulkStatusAction === 'disable') {
      Modal.confirm({
        title: `Отключить выбранных клиентов (${disableCount})?`,
        content: 'При отключении клиентов будут отключены ВСЕ их прокси. Продолжить?',
        okText: 'Отключить',
        cancelText: 'Отмена',
        okType: 'danger',
        onOk: () => handleBulkStatusConfirm(),
      });
      return;
    }

    Modal.confirm({
      title: `Изменить статус выбранных клиентов (${selectedClientIds.length})?`,
      content: `Будет включено: ${enableCount}. Будет отключено: ${disableCount}. Продолжить?`,
      okText: 'Применить',
      cancelText: 'Отмена',
      onOk: () => handleBulkStatusConfirm(),
    });
  };

  const handleSyncClients = async () => {
    try {
      setSyncing(true);
      const response = await apiService.syncKrakenUsers();
      message.success(`Синхронизировано: ${response.data.result.synced_count} новых клиентов, обновлено: ${response.data.result.updated_count}`);
      fetchClients({ force: true });
    } catch (error) {
      message.error(error.response?.data?.detail || 'Ошибка синхронизации клиентов');
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleClientStatus = async (clientId, currentStatus) => {
    try {
      const response = await apiService.toggleClientStatus(clientId);
      const newStatus = response.data.new_status;
      const proxiesAffected = response.data.proxies_affected;
      
      message.success(
        `Клиент ${newStatus ? 'включен' : 'отключен'}. ` +
        `Затронуто прокси: ${proxiesAffected}`
      );
      
      // Обновляем список клиентов
      await fetchClients({ force: true });
    } catch (error) {
      message.error(error.response?.data?.detail || 'Ошибка переключения статуса клиента');
    }
  };

  const handleOpenClientProxies = useCallback((event, client) => {
    const path = buildClientProxiesPath(client);
    if (!path) {
      return;
    }
    if (!isPrimarySpaNavigation(event)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    openAppPathInNewTab(path);
  }, []);

  const handleCreatedAtRangeApply = useCallback((fromStr, toStr) => {
    setCreatedAtFrom(fromStr || null);
    setCreatedAtTo(toStr || null);
  }, []);

  const handleProxyCountRangeApply = useCallback((min, max) => {
    setProxyCountMin(min);
    setProxyCountMax(max);
  }, []);
  const handleOrdersCountRangeApply = useCallback((min, max) => {
    setOrdersCountMin(min);
    setOrdersCountMax(max);
  }, []);
  const handleRolling30RubRangeApply = useCallback((min, max) => {
    setRolling30RubMin(min);
    setRolling30RubMax(max);
  }, []);
  const handleLifetimeRubRangeApply = useCallback((min, max) => {
    setLifetimeRubMin(min);
    setLifetimeRubMax(max);
  }, []);
  const handleBalanceRubRangeApply = useCallback((min, max) => {
    setBalanceRubMin(min);
    setBalanceRubMax(max);
  }, []);
  const handleDiscountPercentRangeApply = useCallback((min, max) => {
    setDiscountPercentMin(min);
    setDiscountPercentMax(max);
  }, []);

  const baseColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      className: 'clients-table__cell clients-table__cell--id',
      filteredValue: idFilter.length ? idFilter : null,
      filterIcon: (filtered) => <FilterFunnelIcon filtered={filtered} />,
      filterDropdown: (props) => (
        <ClientsFilterDropdown title="ID" field="id" {...props} />
      ),
      sorter: (a, b) => (a.id || 0) - (b.id || 0),
      sortOrder: sortConfig.field === 'id' ? sortConfig.order : null,
      render: (id) => <span className="clients-table__cell-text clients-table__cell-text--muted">{String(id).padStart(3, '0')}</span>,
    },
    {
      title: 'Логин',
      dataIndex: 'kraken_proxy_auth_login',
      key: 'login',
      width: 176,
      className: 'clients-table__cell',
      filteredValue: loginFilter.length ? loginFilter : null,
      filterIcon: (filtered) => <FilterFunnelIcon filtered={filtered} />,
      filterDropdown: (props) => (
        <ClientsFilterDropdown title="Логин" field="login" {...props} />
      ),
      sorter: (a, b) => String(getClientKrakenDisplay(a)).localeCompare(String(getClientKrakenDisplay(b)), 'ru'),
      sortOrder: sortConfig.field === 'login' ? sortConfig.order : null,
      render: (_text, record) => (
        <span className="clients-table__cell-text clients-table__cell-text--muted">{getClientKrakenDisplay(record) || '—'}</span>
      ),
    },
    {
      title: 'Логин ЛК',
      dataIndex: 'cabinet_login',
      key: 'cabinet_login',
      width: 176,
      className: 'clients-table__cell',
      filteredValue: cabinetLoginFilter.length ? cabinetLoginFilter : null,
      filterIcon: (filtered) => <FilterFunnelIcon filtered={filtered} />,
      filterDropdown: (props) => (
        <ClientsFilterDropdown title="Логин ЛК" field="cabinet_login" {...props} />
      ),
      sorter: (a, b) =>
        String(a.cabinet_login || a.login || '').localeCompare(String(b.cabinet_login || b.login || ''), 'ru'),
      sortOrder: sortConfig.field === 'cabinet_login' ? sortConfig.order : null,
      render: (val, record) => (
        <span className="clients-table__cell-text clients-table__cell-text--muted">{val || record.login || '—'}</span>
      ),
    },
    {
      title: 'Электронная почта',
      dataIndex: 'email',
      key: 'email',
      width: 260,
      className: 'clients-table__cell',
      filteredValue: emailFilter.length ? emailFilter : null,
      filterIcon: (filtered) => <FilterFunnelIcon filtered={filtered} />,
      filterDropdown: (props) => (
        <ClientsFilterDropdown title="Электронная почта" field="email" {...props} />
      ),
      sorter: (a, b) => String(a.email || '').localeCompare(String(b.email || ''), 'ru'),
      sortOrder: sortConfig.field === 'email' ? sortConfig.order : null,
      render: (_email, record) => (
        <span className="clients-table__cell-text clients-table__cell-text--muted">
          {getClientEmailDisplay(record) || '—'}
        </span>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 144,
      className: 'clients-table__cell clients-table__cell--status',
      align: 'left',
      filters: CLIENT_STATUS_FILTER_OPTIONS,
      filteredValue: statusColumnFilter.length ? statusColumnFilter : null,
      filterIcon: (filtered) => <FilterFunnelIcon filtered={filtered} />,
      filterDropdown: (props) => (
        <ClientsFilterDropdown title="Статус" staticOptions={CLIENT_STATUS_FILTER_OPTIONS} {...props} />
      ),
      sorter: (a, b) => Number(a.is_active) - Number(b.is_active),
      sortOrder: sortConfig.field === 'is_active' ? sortConfig.order : null,
      render: (isActive) => (
        <span className={`clients-status-badge ${isActive ? 'clients-status-badge--active' : 'clients-status-badge--inactive'}`}>
          {isActive ? 'Активен' : 'Неактивен'}
        </span>
      ),
    },
    {
      title: 'Запреты',
      dataIndex: 'skip_forbidden_resources',
      key: 'skip_forbidden_resources',
      width: 128,
      className: 'clients-table__cell clients-table__cell--forbidden',
      align: 'left',
      filters: CLIENT_FORBIDDEN_FILTER_OPTIONS,
      filteredValue: skipForbiddenFilter.length ? skipForbiddenFilter : null,
      filterIcon: (filtered) => <FilterFunnelIcon filtered={filtered} />,
      filterDropdown: (props) => (
        <ClientsFilterDropdown title="Запреты" staticOptions={CLIENT_FORBIDDEN_FILTER_OPTIONS} {...props} />
      ),
      sorter: (a, b) => Number(Boolean(a.skip_forbidden_resources)) - Number(Boolean(b.skip_forbidden_resources)),
      sortOrder: sortConfig.field === 'skip_forbidden_resources' ? sortConfig.order : null,
      render: (skipForbidden) => {
        const isOff = Boolean(skipForbidden);
        return (
          <span className={`clients-status-badge ${isOff ? 'clients-status-badge--inactive' : 'clients-status-badge--active'}`}>
            {isOff ? 'Выкл' : 'Вкл'}
          </span>
        );
      },
    },
    {
      title: 'Прокси',
      dataIndex: 'proxy_count',
      key: 'proxy_count',
      width: 112,
      className: 'clients-table__cell clients-table__cell--proxy',
      filteredValue: proxyCountMin != null || proxyCountMax != null ? ['__proxy_count_range__'] : null,
      filterIcon: (filtered) => <FilterFunnelIcon filtered={filtered} />,
      filterDropdown: (props) => (
        <ClientsNumericRangeFilterDropdown
          visible={props.visible}
          title="Прокси"
          minVal={proxyCountMin}
          maxVal={proxyCountMax}
          onApplyRange={handleProxyCountRangeApply}
          confirm={props.confirm}
          clearFilters={props.clearFilters}
          setSelectedKeys={props.setSelectedKeys}
          selectedKeyToken="__proxy_count_range__"
          inputStep="1"
          minPlaceholder="От, шт."
          maxPlaceholder="До, шт."
        />
      ),
      sorter: (a, b) => (a.proxy_count || 0) - (b.proxy_count || 0),
      sortOrder: sortConfig.field === 'proxy_count' ? sortConfig.order : null,
      render: (count, record) => {
        const proxiesPath = buildClientProxiesPath(record);
        if (!proxiesPath) {
          return count !== undefined && count !== null ? count : 0;
        }
        return (
          <a
            href={proxiesPath}
            className="clients-proxy-count clients-proxy-count--button"
            onClick={(event) => handleOpenClientProxies(event, record)}
            aria-label={`Открыть прокси клиента ${record?.login || ''} в новой вкладке`}
            tabIndex={0}
          >
            {count !== undefined && count !== null ? count : 0}
          </a>
        );
      },
    },
    {
      title: 'Дата создания',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 192,
      className: 'clients-table__cell',
      filteredValue: createdAtFrom || createdAtTo ? ['__created_range__'] : null,
      filterIcon: (filtered) => <FilterFunnelIcon filtered={filtered} />,
      filterDropdown: (props) => (
        <ClientsCreatedAtRangeFilterDropdown
          visible={props.visible}
          from={createdAtFrom}
          to={createdAtTo}
          onApplyRange={handleCreatedAtRangeApply}
          confirm={props.confirm}
          clearFilters={props.clearFilters}
          setSelectedKeys={props.setSelectedKeys}
        />
      ),
      sorter: (a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return ta - tb;
      },
      sortOrder: sortConfig.field === 'created_at' ? sortConfig.order : null,
      render: (date) => (
        <span className="clients-table__cell-text clients-table__cell-text--muted">
          {date ? new Date(date).toLocaleDateString('ru-RU') : 'Не ограничен'}
        </span>
      ),
    },
    {
      title: 'Уровень',
      dataIndex: 'privilege_level',
      key: 'privilege_level',
      width: 144,
      className: 'clients-table__cell clients-table__cell--level',
      filters: CLIENT_PRIVILEGE_FILTER_OPTIONS,
      filteredValue: privilegeLevelFilter.length ? privilegeLevelFilter : null,
      filterIcon: (filtered) => <FilterFunnelIcon filtered={filtered} />,
      filterDropdown: (props) => (
        <ClientsFilterDropdown title="Уровень" staticOptions={CLIENT_PRIVILEGE_FILTER_OPTIONS} {...props} />
      ),
      sorter: (a, b) =>
        String(a.privilege_level || '').localeCompare(String(b.privilege_level || ''), 'ru', { numeric: true }),
      sortOrder: sortConfig.field === 'privilege_level' ? sortConfig.order : null,
      render: (v) => renderPrivilegeLevel(v),
    },
    {
      title: 'Заказов',
      dataIndex: 'orders_count',
      key: 'orders_count',
      width: 144,
      align: 'right',
      className: 'clients-table__cell clients-table__cell--num',
      filteredValue: ordersCountMin != null || ordersCountMax != null ? ['__orders_range__'] : null,
      filterIcon: (filtered) => <FilterFunnelIcon filtered={filtered} />,
      filterDropdown: (props) => (
        <ClientsNumericRangeFilterDropdown
          visible={props.visible}
          title="Заказов"
          minVal={ordersCountMin}
          maxVal={ordersCountMax}
          onApplyRange={handleOrdersCountRangeApply}
          confirm={props.confirm}
          clearFilters={props.clearFilters}
          setSelectedKeys={props.setSelectedKeys}
          selectedKeyToken="__orders_range__"
          inputStep="1"
          minPlaceholder="От, шт."
          maxPlaceholder="До, шт."
        />
      ),
      sorter: (a, b) => (a.orders_count || 0) - (b.orders_count || 0),
      sortOrder: sortConfig.field === 'orders_count' ? sortConfig.order : null,
      render: (n) => <span className="clients-table__cell-text clients-table__cell-text--muted">{n ?? 0}</span>,
    },
    {
      title: 'Все траты, ₽',
      dataIndex: 'privilege_spend_lifetime_rub',
      key: 'privilege_spend_lifetime_rub',
      width: 152,
      align: 'right',
      className: 'clients-table__cell clients-table__cell--num',
      filteredValue: lifetimeRubMin != null || lifetimeRubMax != null ? ['__lifetime_range__'] : null,
      filterIcon: (filtered) => <FilterFunnelIcon filtered={filtered} />,
      filterDropdown: (props) => (
        <ClientsNumericRangeFilterDropdown
          visible={props.visible}
          title="Все траты, ₽"
          minVal={lifetimeRubMin}
          maxVal={lifetimeRubMax}
          onApplyRange={handleLifetimeRubRangeApply}
          confirm={props.confirm}
          clearFilters={props.clearFilters}
          setSelectedKeys={props.setSelectedKeys}
          selectedKeyToken="__lifetime_range__"
          inputStep="1"
          minPlaceholder="От, ₽"
          maxPlaceholder="До, ₽"
        />
      ),
      sorter: (a, b) =>
        (Number(a.privilege_spend_lifetime_rub) || 0) - (Number(b.privilege_spend_lifetime_rub) || 0),
      sortOrder: sortConfig.field === 'privilege_spend_lifetime_rub' ? sortConfig.order : null,
      render: (v) => (
        <span className="clients-table__cell-text clients-table__cell-text--muted">{formatRubInt(v ?? 0)}</span>
      ),
    },
    {
      title: 'Покупки 30 дней, ₽',
      dataIndex: 'privilege_spend_rolling_30d_rub',
      key: 'privilege_spend_rolling_30d_rub',
      width: 160,
      align: 'right',
      className: 'clients-table__cell clients-table__cell--num',
      filteredValue: rolling30RubMin != null || rolling30RubMax != null ? ['__roll30_range__'] : null,
      filterIcon: (filtered) => <FilterFunnelIcon filtered={filtered} />,
      filterDropdown: (props) => (
        <ClientsNumericRangeFilterDropdown
          visible={props.visible}
          title="Покупки 30 дней, ₽"
          minVal={rolling30RubMin}
          maxVal={rolling30RubMax}
          onApplyRange={handleRolling30RubRangeApply}
          confirm={props.confirm}
          clearFilters={props.clearFilters}
          setSelectedKeys={props.setSelectedKeys}
          selectedKeyToken="__roll30_range__"
          inputStep="1"
          minPlaceholder="От, ₽"
          maxPlaceholder="До, ₽"
        />
      ),
      sorter: (a, b) =>
        (Number(a.privilege_spend_rolling_30d_rub) || 0) - (Number(b.privilege_spend_rolling_30d_rub) || 0),
      sortOrder: sortConfig.field === 'privilege_spend_rolling_30d_rub' ? sortConfig.order : null,
      render: (v) => (
        <span className="clients-table__cell-text clients-table__cell-text--muted">{formatRubInt(v ?? 0)}</span>
      ),
    },
    // {
    //   title: 'Продления, ₽',
    //   dataIndex: 'sales_rub',
    //   key: 'sales_rub',
    //   width: 144,
    //   align: 'right',
    //   className: 'clients-table__cell clients-table__cell--num',
    //   filteredValue: salesRubMin != null || salesRubMax != null ? ['__sales_rub_range__'] : null,
    //   filterIcon: (filtered) => <FilterFunnelIcon filtered={filtered} />,
    //   filterDropdown: (props) => (
    //     <ClientsNumericRangeFilterDropdown
    //       visible={props.visible}
    //       title="Продления, ₽"
    //       minVal={salesRubMin}
    //       maxVal={salesRubMax}
    //       onApplyRange={handleSalesRubRangeApply}
    //       confirm={props.confirm}
    //       clearFilters={props.clearFilters}
    //       setSelectedKeys={props.setSelectedKeys}
    //       selectedKeyToken="__sales_rub_range__"
    //       inputStep="1"
    //       minPlaceholder="От, ₽"
    //       maxPlaceholder="До, ₽"
    //     />
    //   ),
    //   sorter: (a, b) => (Number(a.sales_rub) || 0) - (Number(b.sales_rub) || 0),
    //   sortOrder: sortConfig.field === 'sales_rub' ? sortConfig.order : null,
    //   render: (v) => (
    //     <span className="clients-table__cell-text clients-table__cell-text--muted">{formatRubInt(v ?? 0)}</span>
    //   ),
    // },
    {
      title: 'Баланс, ₽',
      dataIndex: 'balance_rub',
      key: 'balance_rub',
      width: 144,
      align: 'right',
      className: 'clients-table__cell clients-table__cell--num',
      filteredValue: balanceRubMin != null || balanceRubMax != null ? ['__balance_rub_range__'] : null,
      filterIcon: (filtered) => <FilterFunnelIcon filtered={filtered} />,
      filterDropdown: (props) => (
        <ClientsNumericRangeFilterDropdown
          visible={props.visible}
          title="Баланс, ₽"
          minVal={balanceRubMin}
          maxVal={balanceRubMax}
          onApplyRange={handleBalanceRubRangeApply}
          confirm={props.confirm}
          clearFilters={props.clearFilters}
          setSelectedKeys={props.setSelectedKeys}
          selectedKeyToken="__balance_rub_range__"
          inputStep="1"
          minPlaceholder="От, ₽"
          maxPlaceholder="До, ₽"
          hint="Пустой баланс в таблице учитывается как 0 ₽"
        />
      ),
      sorter: (a, b) => (Number(a.balance_rub) || 0) - (Number(b.balance_rub) || 0),
      sortOrder: sortConfig.field === 'balance_rub' ? sortConfig.order : null,
      render: (v) => (
        <span
          className={`clients-table__cell-text clients-table__balance ${v == null ? 'clients-table__cell-text--balance-empty' : 'clients-table__cell-text--muted'}`}
        >
          {v == null ? '—' : formatRubInt(v)}
        </span>
      ),
    },
    {
      title: 'Скидка, %',
      dataIndex: 'personal_discount_percent',
      key: 'personal_discount_percent',
      width: 128,
      align: 'right',
      className: 'clients-table__cell clients-table__cell--num',
      filteredValue: discountPercentMin != null || discountPercentMax != null ? ['__discount_range__'] : null,
      filterIcon: (filtered) => <FilterFunnelIcon filtered={filtered} />,
      filterDropdown: (props) => (
        <ClientsNumericRangeFilterDropdown
          visible={props.visible}
          title="Скидка, %"
          minVal={discountPercentMin}
          maxVal={discountPercentMax}
          onApplyRange={handleDiscountPercentRangeApply}
          confirm={props.confirm}
          clearFilters={props.clearFilters}
          setSelectedKeys={props.setSelectedKeys}
          selectedKeyToken="__discount_range__"
          inputStep="0.01"
          minPlaceholder="От, %"
          maxPlaceholder="До, %"
          hint="Без персональной скидки учитывается как 0 %"
        />
      ),
      sorter: (a, b) => (Number(a.personal_discount_percent) || 0) - (Number(b.personal_discount_percent) || 0),
      sortOrder: sortConfig.field === 'personal_discount_percent' ? sortConfig.order : null,
      render: (v) => (
        <span className="clients-table__cell-text clients-table__cell-text--muted">
          {formatPercent(v)}
        </span>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 48,
      fixed: 'right',
      className: 'clients-table__cell clients-table__cell--actions',
      render: (_, record) => {
        const menuItems = [
          {
            key: 'toggle',
            icon: record.is_active ? <PoweroffOutlined /> : <CheckCircleOutlined />,
            label: record.is_active ? 'Отключить' : 'Включить',
            onClick: () => {
              Modal.confirm({
                title: `${record.is_active ? 'Отключить' : 'Включить'} клиента?`,
                content: record.is_active
                  ? 'При отключении клиента будут отключены ВСЕ его прокси. Продолжить?'
                  : 'При включении клиента будут включены ВСЕ его прокси. Продолжить?',
                okText: record.is_active ? 'Отключить' : 'Включить',
                cancelText: 'Отмена',
                okType: record.is_active ? 'danger' : 'primary',
                onOk: () => handleToggleClientStatus(record.id, record.is_active)
              });
            }
          },
          {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Редактировать',
            onClick: () => handleEditClient(record)
          },
          {
            key: 'delete',
            icon: <DeleteIcon />,
            label: 'Удалить',
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: 'Удалить клиента?',
                content: 'ВНИМАНИЕ: Будет выполнено каскадное удаление - клиент и ВСЕ его прокси будут удалены безвозвратно!',
                okText: 'Да, удалить',
                cancelText: 'Отмена',
                okType: 'danger',
                onOk: () => handleDeleteClient(record.id)
              });
            }
          }
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            placement="bottomRight"
            overlayClassName="pm-action-dropdown"
          >
            <button className="pm-action-dots" aria-label="Действия" tabIndex={0} onClick={(e) => e.stopPropagation()}>
              <span className="pm-action-dots__dot" />
              <span className="pm-action-dots__dot" />
              <span className="pm-action-dots__dot" />
            </button>
          </Dropdown>
        );
      }
    },
  ];

  // Столбцы без фиксации
  const columns = baseColumns;

  const handleClientRowClick = useCallback((record, e) => {
    if (
      e.target.closest('.ant-table-selection-column') ||
      e.target.closest('.ant-checkbox-wrapper') ||
      e.target.closest('.pm-action-dots') ||
      e.target.closest('.ant-dropdown') ||
      e.target.closest('.clients-proxy-count') ||
      e.target.closest('.ant-picker') ||
      e.target.closest('.ant-picker-dropdown') ||
      e.target.closest('.cl-filter-dropdown')
    ) {
      return;
    }
    setDetailClient(record);
  }, []);

  const handleClientShiftSelect = (record, selected, _selectedRows, nativeEvent) => {
    const currentKey = record.id;
    if (nativeEvent?.shiftKey && lastSelectedClientKeyRef.current != null) {
      const lastKey = lastSelectedClientKeyRef.current;
      const allKeys = filteredClients.map(c => c.id);
      const currentIdx = allKeys.indexOf(currentKey);
      const lastIdx = allKeys.indexOf(lastKey);

      if (currentIdx !== -1 && lastIdx !== -1) {
        const start = Math.min(currentIdx, lastIdx);
        const end = Math.max(currentIdx, lastIdx);
        const rangeKeys = allKeys.slice(start, end + 1);

        if (selected) {
          const newKeys = [...new Set([...selectedClientIds, ...rangeKeys])];
          setSelectedClientIds(newKeys);
        } else {
          const removeSet = new Set(rangeKeys);
          setSelectedClientIds(selectedClientIds.filter(k => !removeSet.has(k)));
        }
      }
    }
    lastSelectedClientKeyRef.current = currentKey;
  };

  if (loading && clients.length === 0) {
    return (
      <div className="loading-container" role="status" aria-label="Загрузка клиентов">
        <div className="loader-container">
          <div className="loader-ball" />
          <div className="loader-shadow" />
        </div>
      </div>
    );
  }

  return (
    <div className="pm-content-fade-in clients-page">
      <div className="clients-page__head">
        <div className="clients-page__toolbar">
        <h1 className="clients-page__mobile-title">Клиенты</h1>
      {/* ===== Header по SVG-макету ===== */}
      <div className="clients-header">
        <div className="clients-header__left">
          <h1 className="clients-header__title">Клиенты</h1>

          <div className="clients-header__tabs">
            <button
              type="button"
              className={`clients-header__tab${activeFilter === 'all' ? ' clients-header__tab--active' : ''}`}
              onClick={() => { setActiveFilter('all'); setCurrentPage(1); }}
              aria-label="Показать всех клиентов"
              tabIndex="0"
            >
              Все <span className="clients-header__tab-count">{totalCount}</span>
            </button>

            {selectedClientIds.length > 0 && (
              <button
                type="button"
                className={`clients-header__tab clients-header__tab--selected`}
                onClick={() => {/* stays informational — could filter to selected in future */}}
                aria-label={`Выбрано ${selectedClientIds.length} клиентов`}
                tabIndex="0"
              >
                Выбрано <span className="clients-header__tab-count">{selectedClientIds.length}</span>
              </button>
            )}

            <button
              type="button"
              className={`clients-header__tab clients-header__tab--success${activeFilter === 'active' ? ' clients-header__tab--active' : ''}`}
              onClick={() => { setActiveFilter(f => f === 'active' ? 'all' : 'active'); setCurrentPage(1); }}
              aria-label="Показать активных клиентов"
              tabIndex="0"
            >
              <span className="clients-header__tab-text clients-header__tab-text--full">Активные</span>
              <span className="clients-header__tab-text clients-header__tab-text--short">Актив</span>
              <span className="clients-header__tab-count">{activeCount}</span>
            </button>

            <button
              type="button"
              className={`clients-header__tab clients-header__tab--danger${activeFilter === 'inactive' ? ' clients-header__tab--active' : ''}`}
              onClick={() => { setActiveFilter(f => f === 'inactive' ? 'all' : 'inactive'); setCurrentPage(1); }}
              aria-label="Показать неактивных клиентов"
              tabIndex="0"
            >
              <span className="clients-header__tab-text clients-header__tab-text--full">Неактивные</span>
              <span className="clients-header__tab-text clients-header__tab-text--short">Неактив</span>
              <span className="clients-header__tab-count">{inactiveCount}</span>
            </button>

            <button
              type="button"
              className={`clients-header__tab clients-header__tab--newest${sortByNewest ? ' clients-header__tab--active' : ''}`}
              onClick={handleToggleSortByNewest}
              aria-label="Сортировать по дате создания: сначала новые"
              aria-pressed={sortByNewest}
              tabIndex="0"
            >
              Новые
            </button>

            <button
              type="button"
              className={`clients-header__tab clients-header__tab--has-proxies${hasActiveProxiesFilter ? ' clients-header__tab--active' : ''}`}
              onClick={handleToggleHasActiveProxiesFilter}
              aria-label="Показать клиентов с активными прокси"
              aria-pressed={hasActiveProxiesFilter}
              tabIndex="0"
            >
              Есть прокси <span className="clients-header__tab-count">{hasActiveProxiesCount}</span>
            </button>
          </div>
        </div>

        <div className="clients-header__actions">
          {selectedClientIds.length > 0 && (
            <>
              <Tooltip
                title={
                  selectedBulkStatusAction === 'enable'
                    ? `Включить выбранных (${selectedInactiveClientIds.length})`
                    : selectedBulkStatusAction === 'disable'
                      ? `Отключить выбранных (${selectedActiveClientIds.length})`
                      : `Включить ${selectedInactiveClientIds.length}, отключить ${selectedActiveClientIds.length}`
                }
              >
                <button
                  type="button"
                  className="clients-header__action-btn clients-header__action-btn--default"
                  onClick={showBulkStatusConfirm}
                  disabled={bulkStatusUpdating || !selectedBulkStatusAction}
                  aria-label={
                    selectedBulkStatusAction === 'enable'
                      ? 'Включить выбранных клиентов'
                      : selectedBulkStatusAction === 'disable'
                        ? 'Отключить выбранных клиентов'
                        : 'Изменить статус выбранных клиентов'
                  }
                  tabIndex="0"
                  style={{
                    color:
                      selectedBulkStatusAction === 'enable'
                        ? 'var(--pm-success)'
                        : 'var(--pm-warning)',
                  }}
                >
                  {selectedBulkStatusAction === 'enable' ? <CheckCircleOutlined /> : <PoweroffOutlined />}
                </button>
              </Tooltip>

              <Tooltip title={`Удалить выбранных (${selectedClientIds.length})`}>
                <button
                  type="button"
                  className="clients-header__action-btn clients-header__action-btn--default"
                  onClick={showBulkDeleteConfirm}
                  disabled={bulkDeleting}
                  aria-label="Удалить выбранных клиентов"
                  tabIndex="0"
                  style={{ color: 'var(--pm-error)' }}
                >
                  <DeleteIcon />
                </button>
              </Tooltip>

              <Tooltip title={`Редактировать выбранных (${selectedClientIds.length})`}>
                <button
                  type="button"
                  className="clients-header__action-btn clients-header__action-btn--bulk-edit"
                  onClick={handleOpenBulkEdit}
                  disabled={bulkEditing}
                  aria-label={`Редактировать выбранных клиентов (${selectedClientIds.length})`}
                  tabIndex="0"
                >
                  <EditIcon />
                </button>
              </Tooltip>
            </>
          )}

          {clientFilterCount > 0 && (
            <Tooltip title={`Сбросить фильтры (${clientFilterCount})`}>
              <button
                type="button"
                className="clients-header__action-btn clients-header__action-btn--clear-filters"
                onClick={handleClearAllClientFiltersWithSort}
                aria-label={`Сбросить фильтры (${clientFilterCount})`}
                tabIndex="0"
              >
                <ClientsFilterOffIcon />
              </button>
            </Tooltip>
          )}

          <Tooltip title="Синхронизировать с Kraken">
            <button
              type="button"
              className={`clients-header__action-btn clients-header__action-btn--default${syncing ? ' pm-sync-btn--active' : ''}`}
              onClick={handleSyncClients}
              disabled={syncing}
              aria-label="Синхронизировать с Kraken"
              tabIndex="0"
            >
              <SyncIcon />
            </button>
          </Tooltip>

          <Tooltip title="Обновить">
            <button
              type="button"
              className="clients-header__action-btn clients-header__action-btn--default"
              onClick={fetchClients}
              aria-label="Обновить список клиентов"
              tabIndex="0"
            >
              <ReloadIcon />
            </button>
          </Tooltip>

          <Tooltip title="Добавить клиента">
            <button
              type="button"
              className="clients-header__action-btn clients-header__action-btn--primary"
              onClick={() => {
                setEditingClient(null);
                setModalVisible(true);
              }}
              aria-label="Добавить клиента"
              tabIndex="0"
            >
              <PlusIcon />
            </button>
          </Tooltip>
        </div>
      </div>
        </div>
      </div>

      <div className="clients-table-shell">
        <div className="clients-table clients-table--mock">
            <Table
              columns={columns}
              dataSource={paginatedClients}
              loading={{
                spinning: loading,
                indicator: (
                  <div className="loader-container loader-container--sm" role="status" aria-label="Загрузка клиентов">
                    <div className="loader-ball" />
                    <div className="loader-shadow" />
                  </div>
                ),
              }}
              rowKey="id"
              onRow={(record) => ({
                onClick: (e) => handleClientRowClick(record, e),
                style: { cursor: 'pointer' },
              })}
              rowSelection={{
                selectedRowKeys: selectedClientIds,
                onChange: (selectedRowKeys) => setSelectedClientIds(selectedRowKeys),
                onSelect: handleClientShiftSelect,
                preserveSelectedRowKeys: false
              }}
              pagination={false}
              onChange={(_pag, filters, sorter) => {
                const f = filters || {};
                setLoginFilter(Array.isArray(f.login) ? f.login : []);
                setEmailFilter(Array.isArray(f.email) ? f.email : []);
                setIdFilter(Array.isArray(f.id) ? f.id : []);
                setCabinetLoginFilter(Array.isArray(f.cabinet_login) ? f.cabinet_login : []);
                setStatusColumnFilter(Array.isArray(f.is_active) ? f.is_active : []);
                setSkipForbiddenFilter(Array.isArray(f.skip_forbidden_resources) ? f.skip_forbidden_resources : []);
                setPrivilegeLevelFilter(Array.isArray(f.privilege_level) ? f.privilege_level : []);
                const sorterState = Array.isArray(sorter) ? sorter[0] : sorter;
                const nextField = sorterState?.columnKey || sorterState?.field || null;
                const nextOrder = sorterState?.order || null;
                if (!nextField || !nextOrder || nextField === 'actions') {
                  setSortConfig(CLIENTS_DEFAULT_SORT_CONFIG);
                } else {
                  setSortConfig({ field: nextField, order: nextOrder });
                }
                if (sortByNewest && (nextField !== 'created_at' || nextOrder !== 'descend')) {
                  setSortByNewest(false);
                  sortConfigBeforeNewestRef.current = null;
                }
                setCurrentPage(1);
              }}
              scroll={{ x: 2288 }}
              size="middle"
              tableLayout="fixed"
              className="clients-table__antd"
            />

            {/* Custom pagination */}
            <div className="cl-pagination">
              <p className="cl-pagination__info">
                <span className="cl-pagination__range">{rangeStart}-{rangeEnd}</span>
                <span className="cl-pagination__text"> из </span>
                <span className="cl-pagination__total">{totalItems}</span>
                <span className="cl-pagination__text"> записей</span>
              </p>

              <div className="cl-pagination__pages">
                <button type="button" className="cl-pagination__btn" disabled={currentPage <= 1} onClick={() => setCurrentPage(1)} aria-label="Первая страница" tabIndex={0}>
                  <ClChevronDoubleLeft />
                </button>
                <button type="button" className="cl-pagination__btn" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)} aria-label="Предыдущая страница" tabIndex={0}>
                  <ClChevronLeft />
                </button>
                <div className="cl-pagination__current">{currentPage}</div>
                <button type="button" className="cl-pagination__btn" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} aria-label="Следующая страница" tabIndex={0}>
                  <ClChevronRight />
                </button>
                <button type="button" className="cl-pagination__btn" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)} aria-label="Последняя страница" tabIndex={0}>
                  <ClChevronDoubleRight />
                </button>
              </div>

              <ClPerPageDropdown
                value={perPage}
                options={perPageOptions}
                onChange={(val) => { setPerPage(val); setCurrentPage(1); }}
              />
            </div>
        </div>
      </div>

      <ClientCreatePanel
        visible={modalVisible}
        onClose={() => {
          setModalVisible(false);
          setEditingClient(null);
        }}
        onSubmit={handleCreateClient}
        submitting={submitting}
        editingClient={editingClient}
      />

      <ClientBulkEditPanel
        visible={bulkEditVisible}
        selectedCount={selectedClientIds.length}
        clients={selectedClientsForBulk}
        submitting={bulkEditing}
        onClose={handleCloseBulkEdit}
        onSubmit={handleBulkEditSubmit}
      />

      <ClientDetailDrawer
        open={detailClient != null}
        client={detailClient}
        onClose={() => setDetailClient(null)}
        onRefresh={fetchClients}
        onClientUpdated={handleClientUpdatedFromDrawer}
      />

    </div>
  );
};

export default Clients;
